// LogoCreator.tsx - Page-based logo creation (no modals)

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AutoAwesome,
  Upload,
  Check,
  Refresh,
  ArrowBack,
  Close,
} from '@mui/icons-material';

interface LogoCreatorProps {
  companyId: string;
  brandColors?: string[];
  brandTone?: string;
  onLogoCreated: (logoUrl: string) => void;
  onCancel: () => void;
  initialLogo?: string;
}

type Step = 'choose' | 'style' | 'generating' | 'review';

const LOGO_STYLES = [
  { id: 'corporate', label: 'Corporate', icon: '🏢', description: 'Professional, clean, authoritative' },
  { id: 'modern', label: 'Modern', icon: '✨', description: 'Contemporary, sleek, innovative' },
  { id: 'playful', label: 'Playful', icon: '🎨', description: 'Fun, creative, energetic' },
  { id: 'luxury', label: 'Luxury', icon: '💎', description: 'Premium, elegant, sophisticated' },
  { id: 'minimal', label: 'Minimal', icon: '⬜', description: 'Simple, clean, timeless' },
  { id: 'tech', label: 'Tech', icon: '🚀', description: 'Futuristic, digital, dynamic' },
];

const LOGO_COLORS = [
  '#4361EE', '#3F37C9', '#7209B7', '#F72585',
  '#4CC9F0', '#4895EF', '#560BAD', '#B5179E',
  '#F9C74F', '#F9844A', '#F8961E', '#F3722C',
  '#06D6A0', '#118AB2', '#073B4C', '#0B132B',
];

const LogoCreator: React.FC<LogoCreatorProps> = ({
  companyId,
  brandColors = ['#4361EE'],
  brandTone = 'professional',
  onLogoCreated,
  onCancel,
  initialLogo,
}) => {
  const [step, setStep] = useState<Step>(initialLogo ? 'review' : 'choose');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>(brandColors[0] || '#4361EE');
  const [generatedLogos, setGeneratedLogos] = useState<string[]>(initialLogo ? [initialLogo] : []);
  const [selectedLogo, setSelectedLogo] = useState<string>(initialLogo || '');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Mock logo generation
  const handleGenerateLogos = async () => {
    setStep('generating');
    setLoading(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    const mockLogos = [
      `data:image/svg+xml,${encodeURIComponent(generateMockLogo(selectedStyle, selectedColor, 'A'))}`,
      `data:image/svg+xml,${encodeURIComponent(generateMockLogo(selectedStyle, selectedColor, 'B'))}`,
      `data:image/svg+xml,${encodeURIComponent(generateMockLogo(selectedStyle, selectedColor, 'C'))}`,
      `data:image/svg+xml,${encodeURIComponent(generateMockLogo(selectedStyle, selectedColor, 'D'))}`,
    ];

    setGeneratedLogos(mockLogos);
    setStep('review');
    setLoading(false);
  };

  const generateMockLogo = (style: string, color: string, variant: string) => {
    const shapes: Record<string, string> = {
      corporate: `<rect x="30" y="30" width="140" height="140" rx="20" fill="${color}" opacity="0.9"/><text x="100" y="120" font-size="60" text-anchor="middle" fill="white" font-weight="bold">${variant}</text>`,
      modern: `<circle cx="100" cy="100" r="70" fill="${color}" opacity="0.85"/><path d="M60,100 Q100,60 140,100 Q100,140 60,100" fill="white" opacity="0.9"/>`,
      playful: `<rect x="40" y="40" width="120" height="120" rx="30" fill="${color}" transform="rotate(15,100,100)"/><circle cx="100" cy="100" r="40" fill="white" opacity="0.9"/>`,
      luxury: `<path d="M50,160 L100,40 L150,160 L120,160 L100,80 L80,160 Z" fill="${color}"/><rect x="70" y="100" width="60" height="60" rx="5" fill="white" opacity="0.2"/>`,
      minimal: `<rect x="40" y="40" width="120" height="120" rx="10" fill="white" stroke="${color}" stroke-width="4"/><text x="100" y="120" font-size="70" text-anchor="middle" fill="${color}" font-weight="bold">${variant}</text>`,
      tech: `<polygon points="50,40 150,40 180,100 150,160 50,160 20,100" fill="${color}" opacity="0.8"/><circle cx="100" cy="100" r="30" fill="white" opacity="0.9"/>`,
    };

    return `<svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
      ${shapes[style] || shapes.corporate}
    </svg>`;
  };

  const handleConfirmLogo = () => {
    if (selectedLogo) {
      onLogoCreated(selectedLogo);
      setSnackbar({ open: true, message: 'Logo confirmed!', severity: 'success' });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setGeneratedLogos([url]);
      setSelectedLogo(url);
      setStep('review');
      setSnackbar({ open: true, message: 'Logo uploaded successfully!', severity: 'success' });
    };
    reader.readAsDataURL(file);
  };

  // ── Step: Choose ──
  if (step === 'choose') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton size="small" onClick={onCancel}>
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600}>Create Your Logo</Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Choose how you want to create your company logo
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: 'all 0.2s' },
              }}
              onClick={() => setStep('style')}
            >
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <AutoAwesome sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>Generate with AI</Typography>
                <Typography variant="caption" color="textSecondary">
                  Create a custom logo using AI
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: 'all 0.2s' },
              }}
              component="label"
            >
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <Upload sx={{ fontSize: 36, color: 'secondary.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>Upload Logo</Typography>
                <Typography variant="caption" color="textSecondary">
                  Upload PNG, JPG, or SVG
                </Typography>
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileUpload}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={onCancel}>Cancel</Button>
        </Box>
      </Box>
    );
  }

  // ── Step: Style Selection ──
  if (step === 'style') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <IconButton size="small" onClick={() => setStep('choose')}>
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600}>Choose Logo Style</Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          Select a style for your logo
        </Typography>

        <Grid container spacing={1}>
          {LOGO_STYLES.map((style) => (
            <Grid item xs={6} sm={4} key={style.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedStyle === style.id ? '2px solid' : '1px solid',
                  borderColor: selectedStyle === style.id ? 'primary.main' : '#e5e7eb',
                  '&:hover': { boxShadow: 2 },
                }}
                onClick={() => setSelectedStyle(style.id)}
              >
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography sx={{ fontSize: 28 }}>{style.icon}</Typography>
                  <Typography variant="caption" fontWeight={600} display="block">
                    {style.label}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: 9 }}>
                    {style.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
            Choose Primary Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {LOGO_COLORS.map((color) => (
              <Box
                key={color}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: color,
                  cursor: 'pointer',
                  border: selectedColor === color ? '2px solid #000' : '2px solid transparent',
                  '&:hover': { transform: 'scale(1.1)', transition: 'transform 0.2s' },
                }}
                onClick={() => setSelectedColor(color)}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setStep('choose')}>Back</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleGenerateLogos}
            disabled={!selectedStyle}
            startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
          >
            Generate
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Step: Generating ──
  if (step === 'generating') {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="subtitle1" fontWeight={600}>Creating Your Logo</Typography>
        <Typography variant="body2" color="textSecondary">
          Generating multiple logo options...
        </Typography>
      </Box>
    );
  }

  // ── Step: Review ──
  if (step === 'review') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setStep('choose')}>
              <ArrowBack sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600}>Choose Your Logo</Typography>
          </Box>
          <Box>
            <IconButton size="small" onClick={() => { setStep('style'); setGeneratedLogos([]); setSelectedLogo(''); }} title="Try again">
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          Select the logo you prefer
        </Typography>

        <Grid container spacing={1.5}>
          {generatedLogos.map((logo, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Paper
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  border: selectedLogo === logo ? '2px solid' : '1px solid',
                  borderColor: selectedLogo === logo ? 'primary.main' : '#e5e7eb',
                  '&:hover': { boxShadow: 2 },
                  textAlign: 'center',
                  position: 'relative',
                }}
                onClick={() => setSelectedLogo(logo)}
              >
                <img
                  src={logo}
                  alt={`Logo ${index + 1}`}
                  style={{ width: '100%', maxWidth: 120, height: 'auto' }}
                />
                {selectedLogo === logo && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'primary.main',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check sx={{ fontSize: 12, color: '#fff' }} />
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" variant="outlined" startIcon={<Refresh sx={{ fontSize: 14 }} />} onClick={handleGenerateLogos}>
              Regenerate
            </Button>
            <Button size="small" variant="outlined" onClick={() => { setStep('style'); setGeneratedLogos([]); setSelectedLogo(''); }}>
              Change Style
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={onCancel}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleConfirmLogo}
              disabled={!selectedLogo}
              startIcon={<Check sx={{ fontSize: 14 }} />}
            >
              Confirm Logo
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={3000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
    >
      <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
};

export default LogoCreator;